import { StockResult } from "@/src/hooks/useStockDashboard";
import { formatBigNumber } from "@utils/formatBigNumber";

export interface StockDataResult extends StockResult {
  data?: StockResult;
  key?: string;
}

/**
 * 计算股票涨跌幅百分比
 * @param currentPrice 当前价格
 * @param previousClose 前一日收盘价
 * @returns 格式化后的涨跌幅字符串，包含正负号和百分比 2 位小数截取
 */
export const calculateChangeRate: (
  currentPrice: number | undefined,
  previousClose: number | undefined,
) => { value: string; color: string; change?: number; bg?: string } = (
  currentPrice: number | undefined,
  previousClose: number | undefined,
) => {
  // 验证逻辑：
  if (
    currentPrice == null ||
    previousClose == null ||
    isNaN(currentPrice) ||
    isNaN(previousClose) ||
    previousClose === 0
  ) {
    return { value: "--", color: "text-content_secondary", change: 0 };
  }
  const change = currentPrice - previousClose;
  if (change === 0) {
    return { value: "0.00%", color: "text-content_secondary", change: 0 };
  }
  const sign = change > 0 ? "+" : "-";

  // 先计算百分比，然后根据价格范围格式化
  const percentageValue = Math.abs((change / previousClose) * 100);
  const truncated = Math.floor(percentageValue * 100) / 100;
  const percentage = truncated.toFixed(2);

  return {
    value: `${sign}${percentage}%`,
    color:
      change > 0
        ? "text-content_accent1_primary"
        : "text-content_accent2_primary",
    bg:
      change > 0
        ? "bg-background_accent1_overlay20"
        : "bg-background_accent2_overlay20",
    change,
  };
};

/**
 * 计算股票价格变化值
 * @param currentPrice 当前价格
 * @param previousClose 前一日收盘价
 * @returns 格式化后的价格变化字符串，包含正负号和数值
 */
export const calculatePriceChange = (
  currentPrice: number,
  previousClose: number,
): string => {
  const change = currentPrice - previousClose;

  if (change === 0) {
    return "0.00";
  }

  const sign = change > 0 ? "+" : change < 0 ? "-" : "";
  const value = formatPriceByRange(Math.abs(change));
  return `${sign}${value}`;
};

/**
 * 计算股票市值
 * @param currentPrice 当前价格
 * @param sharesOutstanding 流通股数
 * @returns 市值数值
 */
export const calculateMarketCap = (
  currentPrice: number,
  sharesOutstanding: number,
): number => {
  return currentPrice * sharesOutstanding;
};

/**
 * 根据价格范围格式化小数位数（截取，不四舍五入）
 * @param price 价格数值
 * @returns 格式化后的价格字符串
 * @description 价格 >= $1 显示小数点后2位，价格 < $1 显示小数点后4位
 */
export const formatPriceByRange = (price: number): string => {
  if (price >= 1) {
    // 截取到小数点后2位，不四舍五入
    const truncated = Math.floor(price * 100) / 100;
    return formatBigNumber(truncated, {
      precision: 2,
      needFormat: true,
      trimZeros: true,
    });
  } else {
    // 截取到小数点后4位，不四舍五入
    const truncated = Math.floor(price * 10000) / 10000;
    return formatBigNumber(truncated, {
      precision: 4,
      needFormat: true,
      trimZeros: true,
    });
  }
};

export enum SortState {
  NORMAL = "NORMAL",
  DESC = "DESC", //降序
  ASC = "ASC", // 升序
}

export const ImageMap = {
  [SortState.NORMAL]: "/assets/images/sort-normal.svg",
  [SortState.DESC]: "/assets/images/sort-down.svg",
  [SortState.ASC]: "/assets/images/sort-up.svg",
};
